import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';

import { apiPaths } from 'src/services/api/apiPaths';
import { useUserContext } from 'src/components/app/context/UserContext';
import ErrorHandler from 'src/components/common/ErrorHandler';
import type { PageFrameProps } from 'src/components/common/PageFrame';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';

import { DashboardContentWrapperMemo } from './components/DashboardContent';
import DashboardSidebar from './components/DashboardSidebar';

import { styles } from './DashboardPage.styles';

export const DASHBOARD_PAGE_LOADING_INDICATOR_TEST_ID =
  'dashboard-page-loading-indicator';

export interface DashboardPageProps extends PageFrameProps {}

export const GlobalDashboardPage: FC<DashboardPageProps> = ({ sx = [] }) => {
  const [surveyUnset, setSurveyUnset] = useState(false);
  const { unsetSurvey } = useUserContext();
  const { dashboardConfig, loading: dashboardIsLoading } =
    useDashboardContext();

  const loading = useMemo(
    () => dashboardIsLoading || !surveyUnset,
    [dashboardIsLoading, surveyUnset],
  );

  useEffect(() => {
    // unsetSurvey was previously only called on the switch survey page,
    // causing issues when refreshing the global dashboard page.
    unsetSurvey().finally(() => setSurveyUnset(true));
  }, []);

  return (
    <PageFrame
      className="DashboardPage-root"
      sidebar={<DashboardSidebar orgTreeState={null} guidedTourId={''} />}
      sx={[styles.dashboardPage, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {loading && (
        <Backdrop
          data-test-id={DASHBOARD_PAGE_LOADING_INDICATOR_TEST_ID}
          invisible
          open={loading}
          sx={styles.loadingIndicator}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {!loading && !dashboardConfig && (
        <PageNotFound
          headlineKey="pageNotFound.accessDenied"
          explanationKey="pageNotFound.shortExplanation"
        />
      )}

      {!loading && !!dashboardConfig && (
        <DashboardContentWrapperMemo
          dashboardConfig={dashboardConfig}
          showFollowUpInspirations={false}
        />
      )}

      <ErrorHandler
        intercept
        ignoredEndpoints={[
          apiPaths.DASHBOARD.STATISTICS,
          apiPaths.CHATBOT,
          apiPaths.REPORTS.ON_DEMAND,
          apiPaths.DASHBOARD.COMMENTS,
          apiPaths.ORG_UNIT_CHILDREN,
          ...Object.values(apiPaths.VIRTUAL_ASSISTANT),
        ]}
      />
    </PageFrame>
  );
};
